'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ScriptInjectorProps } from '@/components/script-injector/types';
import { Headline } from '@/components/headline/Headline';
import clsx from 'clsx';

/**
 * Browsers do not execute `<script>` tags inserted via `innerHTML` /
 * `dangerouslySetInnerHTML`. CMS embeds (chatbots, calculators, etc.) ship
 * markup + scripts together, so we clone each script into a fresh element
 * after inject — that is what makes the browser run them. Document order is
 * preserved so an inline script that defines a global runs before the
 * external script whose onload calls it.
 */
function activateScripts(container: HTMLElement) {
  const scripts = Array.from(container.querySelectorAll('script'));

  for (const oldScript of scripts) {
    const newScript = document.createElement('script');

    for (const attr of Array.from(oldScript.attributes)) {
      newScript.setAttribute(attr.name, attr.value);
    }

    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  }
}

const ScriptInjector: React.FC<ScriptInjectorProps> = ({
  title,
  htmlContent,
  autoHeightDetect,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Only inject the embed after hydration. The embed script mutates its
  // container as soon as it runs; rendering it on the server lets that
  // mutation happen before React hydrates, causing a hydration mismatch.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!mounted || !container) return;

    container.innerHTML = htmlContent;
    activateScripts(container);

    return () => {
      container.innerHTML = '';
    };
  }, [htmlContent, mounted]);

  useEffect(() => {
    if (!mounted || !autoHeightDetect) return;

    const adjustIframeHeight = () => {
      const iframe = containerRef.current?.querySelector('iframe');
      if (iframe) {
        iframe.onload = () => {
          try {
            const iframeHeight = iframe.height;
            containerRef.current!.style.height = `${iframeHeight}px`;
          } catch (error) {
            console.error(
              "Couldn't access iframe content due to cross-origin restrictions:",
              error,
            );
          }
        };
      }
    };

    adjustIframeHeight();
  }, [htmlContent, autoHeightDetect, mounted]);

  return (
    <div className={clsx(className, 'script-injector flex w-full')}>
      <div className="hidden h-[40rem]"></div>
      <div
        className={clsx(
          className,
          'container mx-auto flex h-screen flex-col gap-6 px-4 sm:px-0',
        )}
      >
        {title && (
          <Headline
            title={title}
            variant="headingTwo"
            level="h2"
            className="text-brand-700"
          />
        )}
        <div className="h-full" ref={containerRef} suppressHydrationWarning />
      </div>
    </div>
  );
};

export default ScriptInjector;
